import { z } from 'zod';
/**
 * Campaign Map Types - World Map with Pins and Fog of War
 *
 * Design Philosophy:
 * - Campaign maps are world/region scale (miles), battle maps are encounter scale (feet)
 * - Coordinates stored as percentages (0-100) to survive map image swaps
 * - Risk-style fog of war with polygon regions
 * - Direct pin-to-battle-map navigation
 */
// =============================================================================
// PIN TYPES
// =============================================================================
/**
 * Pin state controls visibility and interactivity
 */
export const PinStateSchema = z.enum([
    'hidden', // GM only - players cannot see
    'discovered', // Players can see and click
    'completed', // Visual indicator (grayed out, checkmark)
    'active', // Currently at this location (party marker shows here)
]);
/**
 * Pin type determines icon and grouping for region generation
 */
export const PinTypeSchema = z.enum([
    'town',
    'city',
    'village',
    'dungeon',
    'cave',
    'quest',
    'encounter',
    'landmark',
    'shop',
    'tavern',
    'temple',
    'camp',
    'ruins',
    'portal',
    'custom',
]);
/**
 * Pin type categories for region grouping algorithm
 */
export const PIN_TYPE_CATEGORIES = {
    town: 'settlement',
    city: 'settlement',
    village: 'settlement',
    dungeon: 'dungeon',
    cave: 'dungeon',
    quest: 'poi',
    encounter: 'poi',
    landmark: 'poi',
    shop: 'settlement',
    tavern: 'settlement',
    temple: 'poi',
    camp: 'wilderness',
    ruins: 'dungeon',
    portal: 'poi',
    custom: 'poi',
};
// =============================================================================
// CAMPAIGN MAP
// =============================================================================
/**
 * Campaign Map - the world/region map image
 * This is the canvas on which pins are placed
 */
export const CampaignMapSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    name: z.string(),
    // Map image
    imageUrl: z.string().url(),
    imageWidth: z.number().int().positive(), // pixels
    imageHeight: z.number().int().positive(), // pixels
    // World scale (for travel time calculation - optional)
    widthMiles: z.number().positive().optional(),
    heightMiles: z.number().positive().optional(),
    // Is this the active campaign map?
    isActive: z.boolean().default(false),
    // Metadata
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    createdBy: z.string().uuid().optional(),
});
// =============================================================================
// CAMPAIGN PIN
// =============================================================================
/**
 * Campaign Pin - a location marker on the campaign map
 * Pins can link to battle maps for click-to-travel
 */
export const CampaignPinSchema = z.object({
    id: z.string().uuid(),
    campaignMapId: z.string().uuid(),
    // Position as percentage of map image (0-100)
    // Using percentage allows map image swapping without breaking pins
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    // Display
    name: z.string(),
    description: z.string().optional(),
    iconType: PinTypeSchema.default('landmark'),
    customIconUrl: z.string().url().optional(), // Override default icon
    // State
    state: PinStateSchema.default('hidden'),
    // Link to battle map (optional - for click-to-travel)
    linkedBattleMapId: z.string().uuid().optional(),
    linkedSceneId: z.string().uuid().optional(),
    // Region assignment (set after region generation)
    regionId: z.string().uuid().optional(),
    // Metadata
    tags: z.array(z.string()).default([]),
    notes: z.string().optional(), // GM-only notes
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// =============================================================================
// FOG REGIONS
// =============================================================================
/**
 * Vertex in percentage coordinates
 */
export const PercentageVertexSchema = z.object({
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
});
/**
 * Fog Region - a polygon area on the campaign map
 * Risk-style regions that can be revealed by the GM
 */
export const FogRegionSchema = z.object({
    id: z.string().uuid(),
    campaignMapId: z.string().uuid(),
    // Name for GM reference (e.g., "Northern Wastes", "Goblin Territory")
    name: z.string().optional(),
    // Polygon vertices in percentage coordinates (0-100)
    // Minimum 3 vertices for a valid polygon
    vertices: z.array(PercentageVertexSchema).min(3),
    // Reveal state
    isRevealed: z.boolean().default(false),
    // Was this region auto-generated or manually drawn?
    autoGenerated: z.boolean().default(false),
    // Visual customization
    color: z.string().optional(), // Optional tint color for revealed areas
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// =============================================================================
// PARTY LOCATION
// =============================================================================
/**
 * Party Location - tracks where the party is on the campaign map
 * Can be at a pin or at a free position
 */
export const PartyLocationSchema = z.object({
    campaignMapId: z.string().uuid(),
    // At a specific pin
    currentPinId: z.string().uuid().optional(),
    // Or at a free position (percentage coordinates)
    x: z.number().min(0).max(100).optional(),
    y: z.number().min(0).max(100).optional(),
    updatedAt: z.coerce.date(),
});
// =============================================================================
// REGION GENERATION CONFIG
// =============================================================================
/**
 * Configuration for auto-generating fog regions
 */
export const RegionGenerationConfigSchema = z.object({
    // Minimum region size as percentage of total map area
    minRegionPercent: z.number().min(1).max(50).default(5),
    // Maximum region size as percentage of total map area
    maxRegionPercent: z.number().min(5).max(100).default(25),
    // Distance threshold for clustering nearby pins (percentage)
    clusterThreshold: z.number().min(1).max(30).default(10),
    // Whether to group by pin type category
    groupByType: z.boolean().default(true),
});
// =============================================================================
// API INPUT/OUTPUT TYPES
// =============================================================================
/**
 * Input for creating a new campaign map
 */
export const CreateCampaignMapInputSchema = z.object({
    campaignId: z.string().uuid(),
    name: z.string(),
    imageUrl: z.string().url(),
    imageWidth: z.number().int().positive(),
    imageHeight: z.number().int().positive(),
    widthMiles: z.number().positive().optional(),
    heightMiles: z.number().positive().optional(),
});
/**
 * Input for creating a new pin
 */
export const CreatePinInputSchema = z.object({
    campaignMapId: z.string().uuid(),
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    name: z.string(),
    description: z.string().optional(),
    iconType: PinTypeSchema.default('landmark'),
    state: PinStateSchema.default('hidden'),
    linkedBattleMapId: z.string().uuid().optional(),
    linkedSceneId: z.string().uuid().optional(),
    tags: z.array(z.string()).default([]),
    notes: z.string().optional(),
});
/**
 * Input for updating a pin
 */
export const UpdatePinInputSchema = z.object({
    pinId: z.string().uuid(),
    x: z.number().min(0).max(100).optional(),
    y: z.number().min(0).max(100).optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    iconType: PinTypeSchema.optional(),
    state: PinStateSchema.optional(),
    linkedBattleMapId: z.string().uuid().nullable().optional(),
    linkedSceneId: z.string().uuid().nullable().optional(),
    regionId: z.string().uuid().nullable().optional(),
    tags: z.array(z.string()).optional(),
    notes: z.string().nullable().optional(),
});
/**
 * Input for creating/updating a fog region
 */
export const UpsertRegionInputSchema = z.object({
    id: z.string().uuid().optional(), // If provided, update existing
    campaignMapId: z.string().uuid(),
    name: z.string().optional(),
    vertices: z.array(PercentageVertexSchema).min(3),
    isRevealed: z.boolean().optional(),
    color: z.string().optional(),
});
// =============================================================================
// HELPERS
// =============================================================================
/**
 * Get the default icon for a pin type
 */
export function getPinTypeIcon(type) {
    const icons = {
        town: 'home',
        city: 'building',
        village: 'house',
        dungeon: 'skull',
        cave: 'mountain',
        quest: 'star',
        encounter: 'swords',
        landmark: 'flag',
        shop: 'shopping-bag',
        tavern: 'beer',
        temple: 'cross',
        camp: 'tent',
        ruins: 'columns',
        portal: 'sparkles',
        custom: 'map-pin',
    };
    return icons[type];
}
/**
 * Get the display color for a pin state
 */
export function getPinStateColor(state) {
    const colors = {
        hidden: '#6b7280', // gray-500
        discovered: '#3b82f6', // blue-500
        completed: '#22c55e', // green-500
        active: '#f59e0b', // amber-500
    };
    return colors[state];
}
/**
 * Check if a point is inside a polygon (ray casting algorithm)
 */
export function pointInPolygon(point, vertices) {
    let inside = false;
    const n = vertices.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = vertices[i].x;
        const yi = vertices[i].y;
        const xj = vertices[j].x;
        const yj = vertices[j].y;
        if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}
/**
 * Calculate the area of a polygon (in percentage^2)
 */
export function calculatePolygonArea(vertices) {
    let area = 0;
    const n = vertices.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += vertices[i].x * vertices[j].y;
        area -= vertices[j].x * vertices[i].y;
    }
    return Math.abs(area / 2);
}
/**
 * Get the centroid of a polygon
 */
export function getPolygonCentroid(vertices) {
    let cx = 0;
    let cy = 0;
    const n = vertices.length;
    for (const v of vertices) {
        cx += v.x;
        cy += v.y;
    }
    return { x: cx / n, y: cy / n };
}
//# sourceMappingURL=campaign-map-types.js.map